import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    ConfiguratorTaskService,
    DataFileDto,
    EngineEtlService,
    FieldTable,
    ImportLineCountersDto,
    ImportLineDto,
    ImportLineReplaceResultDto,
    ImportLineSearchCriteriaDto,
    ImportLineSearchMatchDto,
    ImportLineSearchResultsDto,
    ImportRunDto,
    PreviewImportResult,
    TableLiteDto,
    TaskCompteur,
    TaskDTO,
    TaskLog
} from '@tj/tj-dmp-lib-api';
import { ConfirmationService, MenuItem, MessageService, SelectItem } from 'primeng/api';
import { DmpWebsocketService } from '../../../services/dmp-websocket.service';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { Subscription } from 'rxjs/internal/Subscription';
import { TranslateService } from '@ngx-translate/core';
import { EtlUpdateDesignService } from '../etl-update/etl-update-design2/etl-update-design.service';
import { catchError, retry, switchMap, takeWhile } from 'rxjs/operators';
import { EMPTY, interval, of } from 'rxjs';
import { UtilsService } from '../../../services/utils.service';
import { ImportStep } from '../../../components/import-steps/import-steps.component';
import { AccountService } from '../../../auth/account.service';

@Component({
    selector: 'dmp-etl-preview',
    templateUrl: './etl-task-preview.component.html',
    styleUrls: ['./etl-task-preview.component.scss'],
    standalone: false
})
export class EtlTaskPreviewComponent implements OnInit, OnDestroy {
    breadcrumb: MenuItem[] = [
        { label: this.translate.instant('menu.flow.label') },
        { label: this.translate.instant('menu.flow.tasks'), routerLink: '/h/flux/import-v1/task' },
    ];
    description = this.translate.instant('etl.preview.pageDescription');
    display = true;
    /**
     * Filtre principal de l'écran, toujours actif (jamais désactivé : l'absence de ligne
     * correspondante se traduit simplement par une liste vide). Les lignes déjà `IMPORTED` ne sont
     * jamais affichées ici : leur nombre est déjà dans les compteurs, les revoir une par une
     * n'apporte rien une fois chargées.
     */
    rowFilterMode: 'all' | 'pendingOnly' | 'errorOnly' = 'all';
    rowFilterModeOptions: SelectItem[] = [
        { label: this.translate.instant('etl.preview.toolbar.allRows'), value: 'all' },
        { label: this.translate.instant('etl.preview.toolbar.pendingOnly'), value: 'pendingOnly' },
        { label: this.translate.instant('etl.preview.toolbar.errorOnly'), value: 'errorOnly' }
    ];
    /** États `ImportLineDto.status` correspondant à chaque mode ; `IMPORTED` n'y figure jamais. */
    private static readonly ROW_FILTER_STATUSES: { [mode: string]: string[] } = {
        all: ['PENDING', 'ERROR'],
        pendingOnly: ['PENDING'],
        errorOnly: ['ERROR']
    };
    /**
     * Restreint en plus aux lignes portant ce type d'erreur précis ('CAT:<clé>' ou 'FIELD:<fieldSrc>').
     * `null` = « Aucun filtre » (état neutre, hors mode « en erreur ») ; {@link ERROR_TYPE_ALL} =
     * « Toutes les erreurs » (mode « en erreur », sans restriction de type).
     */
    errorTypeFilter: string = null;
    /** Valeur de {@link errorTypeFilter} pour « Toutes les erreurs » : distincte de `null` pour que
     * les deux filtres restent synchronisés (cf. onRowFilterModeChange / onErrorTypeFilterChange). */
    private static readonly ERROR_TYPE_ALL = '';
    /** Le filtre "Type d'erreur" n'a de sens que s'il reste des lignes en erreur à isoler ; sinon il reste visible mais désactivé. */
    get hasErrorRows(): boolean {
        // `counters` (ventilation par ligne, via getTaskRowCounters) n'est rafraîchi qu'au
        // maximum toutes les 10 s pendant un traitement en cours (cf. refreshCounters) : sans
        // repli sur `task.failures` (mis à jour à chaque tick de polling, sans throttle), le
        // filtre resterait désactivé jusqu'à 10 s après que le résultat l'ait déjà affiché.
        if (this.counters && this.counters.errors > 0) {
            return true;
        }
        return !!(this.task && this.task.failures > 0);
    }
    errorTypeOptions: SelectItem[] = [];

    /**
     * Dernière entrée de l'historique (cf. {@code task.logsCompteur}), pour la carte « Dernière
     * exécution » : `task.successes`/`task.failures`/`task.dateFin` sont un cumul sur toute la vie
     * de la tâche (validations comprises), pas le résultat de la dernière exécution — d'où l'usage
     * de l'historique plutôt que ces champs.
     */
    get lastRun(): TaskCompteur {
        const log = this.task?.logsCompteur;
        return log && log.length > 0 ? log[log.length - 1] : null;
    }

    /** Historique du plus récent au plus ancien (task.logsCompteur est rempli en ordre chronologique). */
    get logsCompteurDesc(): TaskCompteur[] {
        const log = this.task?.logsCompteur;
        return log ? [...log].reverse() : [];
    }

    res: PreviewImportResult;
    task: TaskDTO;

    /** Journal de la tâche, séparé une fois par chargement : INFO (ex. lignes par affilié d'un export séparé) / le reste. */
    private splitLogsSource: TaskLog[];
    private splitLogsResult: { info: TaskLog[]; problems: TaskLog[] } = {info: [], problems: []};

    private splitLogs(): { info: TaskLog[]; problems: TaskLog[] } {
        const logs = this.task?.logs;
        if (logs !== this.splitLogsSource) {
            this.splitLogsSource = logs;
            this.splitLogsResult = {
                info: (logs ?? []).filter(l => l.level === 'INFO'),
                problems: (logs ?? []).filter(l => l.level !== 'INFO')
            };
        }
        return this.splitLogsResult;
    }

    get infoLogs(): TaskLog[] {
        return this.splitLogs().info;
    }

    get problemLogs(): TaskLog[] {
        return this.splitLogs().problems;
    }

    /**
     * Page courante de lignes. Les lignes d'un import vivent en base (tools_import_line) et sont
     * paginées côté serveur : le composant n'en tient jamais plus d'une page, là où il chargeait
     * l'intégralité du tableau — inutilisable le jour où le mapping casse et où les 182 725 lignes
     * basculent en erreur d'un coup, c'est-à-dire quand l'écran est le plus nécessaire.
     */
    pageRows: ScreenRow[] = [];
    /** Colonnes et mapping vers les tables destination, servis avec la page. */
    pageDataFile: DataFileDto;
    /** Compteurs et ventilation par type d'erreur, agrégés sur TOUTE la tâche. */
    counters: ImportLineCountersDto;
    pageSize = 25;
    pageSizeOptions: SelectItem[] = [
        { label: this.translate.instant('etl.preview.toolbar.rowsOption', { count: 25 }), value: 25 },
        { label: this.translate.instant('etl.preview.toolbar.rowsOption', { count: 50 }), value: 50 },
        { label: this.translate.instant('etl.preview.toolbar.rowsOption', { count: 100 }), value: 100 }
    ];
    /** Nombre de lignes correspondant aux filtres, sur toute la tâche. */
    totalFiltered = 0;
    hasMore = false;
    loadingRows = false;
    /** Numéro de ligne du fichier source à atteindre directement. */
    goToRowIndex: number = null;
    /**
     * Curseurs des pages déjà affichées (pagination par keyset) : le serveur pagine sur le numéro
     * de ligne et non par décalage, un OFFSET profond faisant parcourir au serveur toutes les
     * lignes sautées. On empile donc les curseurs pour pouvoir revenir en arrière.
     */
    private cursors: Array<number> = [];
    /**
     * Le suivi d'une tâche en cours interroge la tâche toutes les 2 secondes. Les compteurs, eux,
     * viennent d'un GROUP BY sur la table des lignes : les rafraîchir à cette cadence ferait un
     * agrégat par seconde sur des centaines de milliers de lignes. On les espace donc, sans
     * ralentir le suivi du statut lui-même.
     */
    private static readonly COUNTERS_REFRESH_MIN_MS = 10000;
    /**
     * Pendant un traitement, les compteurs alimentent la carte de progression : 10 s la rendrait
     * saccadée. Le GROUP BY est couvert par l'index (task_id, status, row_index), on peut se
     * permettre une cadence plus serrée.
     */
    private static readonly IMPORT_COUNTERS_REFRESH_MIN_MS = 3000;
    /** Tentatives supplémentaires d'un appel de suivi avant de considérer la tâche injoignable. */
    private static readonly POLLING_RETRIES = 3;
    private lastCountersRefresh = 0;
    private lastStatus: string = null;

    tables: SelectItem[];
    cols: ItemCol[];
    dateCreation: string;
    dateFin: string;
    dateExec: string;
    datastore: string;

    delay = 2000;
    /**
     * Durée maximale d'une tâche IN_PROGRESS <b>sans progression</b>. Au-delà, on considère que le
     * traitement qui la portait a disparu : l'état d'exécution vit en mémoire dans l'engine, un
     * redémarrage laisse la tâche IN_PROGRESS en base sans que personne ne la fasse plus progresser.
     * Mesurée depuis la dernière avancée et non depuis le lancement : un gros import dure plus
     * longtemps que ce délai tout en progressant normalement.
     */
    pollingTimeoutMs = 15 * 60 * 1000;
    /** Vrai quand le suivi a été abandonné : on affiche un état explicite au lieu du loader. */
    pollingTimedOut = false;

    /** Nom pleinement qualifié d'une exception ou d'une erreur Java, ex. feign.codec.DecodeException. */
    private static readonly JAVA_THROWABLE = /(?:[a-z][\w$]*\.)+[A-Z][\w$]*(?:Exception|Error)\b/;

    private subs: Array<Subscription> = [];
    private nIntervId: any;
    private id!: string;
    private updateValue = {};
    protected isExport = false;

    constructor(
        private readonly translate: TranslateService,
        private route: ActivatedRoute,
        private router: Router,
        private dmpWebsocketService: DmpWebsocketService,
        private configuratorTaskService: ConfiguratorTaskService,
        private engineEtlService: EngineEtlService,
        private etlUpdateDesignService: EtlUpdateDesignService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private readonly utilsService: UtilsService,
        private readonly accountService: AccountService) {
        // Les libellés ci-dessus sont figés à la construction (translate.instant) : si la langue
        // du compte se résout après coup (cf. DMP2-833), ce rafraîchissement les remet à jour.
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedOptions());
    }

    private rebuildTranslatedOptions(): void {
        this.breadcrumb = [
            { label: this.translate.instant('menu.flow.label') },
            { label: this.translate.instant('menu.flow.tasks'), routerLink: '/h/flux/import-v1/task' },
        ];
        this.description = this.translate.instant('etl.preview.pageDescription');
        this.rowFilterModeOptions = [
            { label: this.translate.instant('etl.preview.toolbar.allRows'), value: 'all' },
            { label: this.translate.instant('etl.preview.toolbar.pendingOnly'), value: 'pendingOnly' },
            { label: this.translate.instant('etl.preview.toolbar.errorOnly'), value: 'errorOnly' }
        ];
        this.pageSizeOptions = [
            { label: this.translate.instant('etl.preview.toolbar.rowsOption', { count: 25 }), value: 25 },
            { label: this.translate.instant('etl.preview.toolbar.rowsOption', { count: 50 }), value: 50 },
            { label: this.translate.instant('etl.preview.toolbar.rowsOption', { count: 100 }), value: 100 }
        ];
        this.searchTypeOptions = [
            { label: this.translate.instant('etl.preview.search.textOption'), value: 'text' },
            { label: this.translate.instant('etl.preview.search.emptyOption'), value: 'empty' }
        ];
        this.replaceTypeOptions = [
            { label: this.translate.instant('etl.preview.search.firstOccurrenceOption'), value: 'first' },
            { label: this.translate.instant('etl.preview.search.allOccurrencesOption'), value: 'all' },
            { label: this.translate.instant('etl.preview.search.overwriteOption'), value: 'overwrite' }
        ];
        this.replaceValueSourceOptions = [
            { label: this.translate.instant('etl.preview.search.manualTextOption'), value: 'manual' },
            { label: this.translate.instant('etl.preview.search.columnValueOption'), value: 'column' }
        ];
        this.searchScopeOptions = [
            { label: this.translate.instant('etl.preview.toolbar.allRows'), value: null },
            { label: this.translate.instant('etl.preview.toolbar.pendingOnly'), value: 'PENDING' },
            { label: this.translate.instant('etl.preview.toolbar.errorOnly'), value: 'ERROR' }
        ];
        this.buildErrorTypeOptions();
    }

    private getData(id: string): Promise<TaskDTO> {
        return new Promise((resolve, reject) => {
            this.configuratorTaskService.getTaskUsingGET(this.datastore, id).subscribe(res => {
                this.isExport = res.type === 'EXPORT_DATA';
                this.setTask(res);
                this.updateType();
                this.display = true;
                if (!this.isExport) {
                    // Les lignes ne sont plus portées par la tâche : compteurs et page sont
                    // chargés à part. Pendant un import, on ne les recharge pas à chaque tick de
                    // suivi — seulement au premier passage, à chaque changement de statut, et de
                    // loin en loin.
                    const statusChanged = this.lastStatus !== this.task.status;
                    const firstLoad = this.lastStatus === null;
                    this.lastStatus = this.task.status;
                    if (firstLoad || statusChanged) {
                        this.refreshCounters(true);
                        this.loadFirstPage();
                    } else {
                        this.refreshCounters(false);
                    }
                }
                resolve(this.task);
            }, error => {
                // Sans cette branche la promesse ne se règle jamais : le polling continuerait
                // à tourner sur une tâche devenue injoignable.
                reject(error);
            });
        });
    }

    /**
     * Remplace la tâche affichée, dates normalisées — dont celles de logsCompteur, sans quoi le pipe
     * `date` lit un timestamp brut en secondes comme des millisecondes et affiche une date proche de
     * 1970.
     */
    private setTask(res: TaskDTO) {
        this.task = {
            ...res,
            dateCreation: this.utilsService.normalizeTimestampToDate(res.dateCreation),
            dateFin: this.utilsService.normalizeTimestampToDate(res.dateFin),
            dateExec: this.utilsService.normalizeTimestampToDate(res.dateExec),
            logsCompteur: res.logsCompteur?.map(compteur => ({
                ...compteur,
                date: this.utilsService.normalizeTimestampToDate(compteur.date),
                dateExec: this.utilsService.normalizeTimestampToDate(compteur.dateExec)
            }))
        };
        this.recomputeRunView();
    }

    ngOnInit(): void {
        this.id = this.route.snapshot.paramMap.get('id');
        this.datastore = this.route.snapshot.paramMap.get('tenant');
        /*this.subs.push(this.dmpWebsocketService.listenerUpdateDataTaskObserver.subscribe(task => {
            if (task.id === id && task.status !== 'IN_PROGRESS') {
                this.getData(this.task.id);
            } else if (task.id === id) {
                this.task.status = task.status;
                this.task.nbTotalLines = task.nbTotalLines;
                this.task.successes = task.successes;
                this.task.failures = task.failures;
            }
        }));*/

        this.getData(this.id).then(t => {
            if (t.status === 'IN_PROGRESS') {
                this.startPolling();
            }
        }).catch(() => this.giveUpPolling());
    }

    private startPolling(): void {
        this.nIntervId = setInterval(() => {
            this.getData(this.id).then(t => {
                if (t.status !== 'IN_PROGRESS') {
                    this.stopPolling();
                } else {
                    this.checkStalled();
                }
            }).catch(() => this.giveUpPolling());
        }, this.delay);
    }

    private stopPolling(): void {
        if (this.nIntervId) {
            clearInterval(this.nIntervId);
            this.nIntervId = null;
        }
    }

    /**
     * Abandonne le suivi si rien n'a progressé depuis pollingTimeoutMs. Les suivis lancés par
     * doCheck() et doImport() sont sinon sans borne : si l'engine disparaît en cours de traitement,
     * ils interrogent la tâche indéfiniment.
     */
    private checkStalled(): void {
        if (!this.pollingTimedOut && Date.now() - this.lastActivityAt > this.pollingTimeoutMs) {
            this.giveUpPolling();
        }
    }

    private giveUpPolling(): void {
        this.stopPolling();
        this.pollingTimedOut = true;
        // Pas de récapitulatif : on ne sait pas comment le traitement s'est terminé.
        this.cancelRunTracking();
        this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('etl.preview.polling.interruptedSummary'),
            detail: this.translate.instant('etl.preview.polling.interruptedDetail')
        });
    }

    ngOnDestroy(): void {
        if (this.subs) {
            this.subs.forEach(i => i.unsubscribe());
        }
        this.stopPolling();
    }

    /**
     * Motif affiché à l'utilisateur, tiré de la première ligne de stackTrace.
     *
     * Une trace technique — exception Java, erreur de désérialisation Feign — ne lui apprend
     * rien et l'inquiète pour rien : on lui substitue une phrase, et on le renvoie vers le
     * panneau « Autres erreurs » qui contient le détail complet à l'usage du support.
     * Les motifs déjà rédigés en français, comme celui d'une tâche interrompue par un
     * redémarrage de l'engine, sont affichés tels quels.
     */
    get statusReason(): string {
        const trace = this.task?.stackTrace;
        if (!trace) {
            return null;
        }
        const firstLine = trace.split('\n')[0].trim();
        return EtlTaskPreviewComponent.JAVA_THROWABLE.test(firstLine)
            ? this.translate.instant('etl.preview.summary.technicalFailureReason')
            : firstLine;
    }

    isLoadingTable() {
        return 'IN_PROGRESS' === this.task?.status && !this.pollingTimedOut;
    }

    updateTable() {
        this.tables = [];
        this.cols = [];
        if (this.pageDataFile && this.pageDataFile.meta) {
            // Colonnes non réduites : l'index d'une cellule à l'écran est sa position dans
            // row_data, ce qui rend la correction par index fiable côté serveur.
            const dataColumn = this.pageDataFile.column;
            for (const [keyTable, tableFields] of Object.entries(this.pageDataFile.meta)) {
                if (Object.keys(tableFields.fieldParCodeSource).length > 0) {
                    let length = 0;
                    // tslint:disable-next-line:forin
                    const colsByTable: Array<ItemCol> = [];
                    for (const src in tableFields.fieldParCodeSource) {
                        // il est possible d'avoir un champs source pour plusieurs destinations
                        tableFields.fieldParCodeSource[src].forEach(field => {
                            length++;
                            const index = dataColumn.findIndex(value =>
                                value.headerSrc === src &&
                                keyTable.includes(value.headerDest.table) &&
                                value.headerDest.field === field.code);
                            colsByTable.push({
                                source: src,
                                field,
                                indexData: index,
                                tooltip: this.buildTootltip(tableFields.table, field)
                            } as ItemCol);
                        });
                    }
                    const table: SelectItem = {
                        label: tableFields.table.label || tableFields.table.code,
                        value: length,
                        title: this.buildTootltip(tableFields.table)
                    };
                    this.tables.push(table);
                    this.reorganizeColumns(colsByTable, table);
                    this.cols.push(...colsByTable);
                }
            }
        }
        this.prepareSearchableColumns();
    }

    reorganizeColumns(cols: Array<ItemCol>, table: SelectItem) {
        if (table.label === 'passenger') {
            const order = ['civility', 'firstname', 'lastname', 'birthdate'];
            cols.sort((a, b) => {
                const indexA = order.indexOf(a.field.code);
                const indexB = order.indexOf(b.field.code);
                return indexA - indexB;
            });
        } else {
            cols.sort((a, b) => a.indexData - b.indexData);
        }
    }

    private updateType() {
        if (this.task) {
            const formatDateTime = 'DD/MM/YYYY HH:mm:ss';
            this.dateFin = this.task.dateFin ?
                moment(this.task.dateFin).format(formatDateTime) : null;
            this.dateCreation = this.task.dateCreation ?
                moment(this.task.dateCreation).format(formatDateTime) : null;
            this.dateExec = this.task.dateExec ?
                moment(this.task.dateExec).format(formatDateTime) : null;
        }
    }

    /**
     * Erreurs d'une ligne, pour l'info-bulle (HTML — l'affichage colore séparément colonne, valeur
     * et motif, cf. {@code [escape]="false"} côté template) : une entrée par échec, avec le motif
     * traduit via {@link errorTypeLabel} à partir de {@code errorDetails.key} — plutôt que
     * {@code errors}, où le serveur fige le message en français. Le contenu dynamique (valeurs du
     * fichier importé, message technique) est échappé avant insertion : ce n'est pas du texte de
     * confiance.
     *
     * Une ligne en échec technique au chargement (pas une erreur de validation catégorisée, ex.
     * une exception SQL) n'a pas de {@code errorDetails} : {@code errors} (brut, en français, peut
     * exposer un détail technique interne — nom de table/colonne, contrainte SQL) reste alors le
     * seul contenu disponible. Réservé aux super admins ; les autres utilisateurs voient un message
     * générique plutôt que ce détail technique.
     */
    getError(row: ScreenRow) {
        const details = row.line.errorDetails || [];
        if (details.length) {
            return details.map(d => {
                // Libellé métier de la colonne (ex. « Id Affilié »), comme dans l'en-tête du
                // tableau, avec la colonne source du fichier entre parenthèses (utile pour la
                // retrouver dans le fichier importé) — repli sur les codes bruts si la colonne
                // destination n'est pas trouvée.
                const matchingCol = (this.cols || []).find(c => c.source === d.fieldSrc);
                const column = this.escapeHtml(matchingCol
                    ? `${matchingCol.field.label || matchingCol.source} (${d.fieldSrc})`
                    : `${d.fieldDest}/${d.fieldSrc}`);
                const value = this.escapeHtml(d.data);
                const message = this.escapeHtml(this.errorTypeLabel(d.key));
                return `<span class="error-tooltip-message">${message}</span> : `
                    + `<span class="error-tooltip-column">${column}</span> `
                    + `<span class="error-tooltip-value">"${value}"</span>`;
            }).join('<br>');
        }
        if (!(row.line.errors || []).length) {
            return '';
        }
        if (this.accountService.isSuperAdmin()) {
            return row.line.errors.map(e => this.escapeHtml(e)).join('<br>');
        }
        return this.escapeHtml(this.translate.instant('etl.preview.table.technicalErrorGeneric'));
    }

    private escapeHtml(text: string): string {
        return (text ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    isRowInError(row: ScreenRow) {
        return row.line.status === 'ERROR';
    }

    /** Vrai si cette cellule précise est la source d'une des erreurs de validation de la ligne. */
    isCellInError(row: ScreenRow, col: ItemCol): boolean {
        const details = row.line.errorDetails || [];
        return details.some(d => d.fieldSrc === col.source);
    }

    /** Validation : rien à revalider si toutes les lignes sont déjà en attente propre ou importées. */
    get canValidate(): boolean {
        return this.task?.status !== 'IN_PROGRESS' && !!this.counters
            && ((this.counters.pending || 0) > 0 || (this.counters.errors || 0) > 0);
    }

    /** Import : seules les lignes PENDING sont chargées, inutile de relancer s'il n'y en a aucune. */
    get canImport(): boolean {
        return this.task?.status !== 'IN_PROGRESS' && !!this.counters && (this.counters.pending || 0) > 0;
    }

    doCheck() {
        // Rien à synchroniser : chaque correction a déjà été persistée par son propre appel.
        this.startLocalRun('VALIDATION');
        this.engineEtlService.updateValidation(this.datastore, this.task.id).pipe(
            switchMap(() => interval(this.delay).pipe(
                switchMap(() => this.getData(this.id)),
                // Un appel de suivi qui échoue ne dit rien du traitement, qui tourne côté serveur :
                // on réessaie quelques fois avant de renoncer, au lieu de tout arrêter au premier
                // hoquet réseau.
                retry({ count: EtlTaskPreviewComponent.POLLING_RETRIES, delay: this.delay }),
                takeWhile(data => {
                    this.checkStalled();
                    return data.status === 'IN_PROGRESS' && !this.pollingTimedOut;
                }),
                catchError(() => {
                    this.giveUpPolling();
                    return EMPTY;
                })
            )),
            catchError(err => {
                this.onRunLaunchFailed(err, this.translate.instant('etl.preview.search.launchFailedValidation'));
                return of(null);
            })
        ).subscribe();
    }

    doImport() {
        this.startLocalRun('LOAD');
        this.engineEtlService.importTask(this.datastore, this.task.id).pipe(
            switchMap(() => interval(this.delay).pipe(
                switchMap(() => this.configuratorTaskService.getTaskUsingGET(this.datastore, this.id)),
                // Cf. doCheck : l'import continue côté serveur même si un appel de suivi échoue.
                retry({ count: EtlTaskPreviewComponent.POLLING_RETRIES, delay: this.delay }),
                takeWhile(data => {
                    this.setTask(data);
                    if (data.status !== 'IN_PROGRESS') {
                        return false;
                    }
                    // Ce suivi ne passe pas par getData() : sans cet appel, les compteurs (et donc
                    // la progression) resteraient figés jusqu'à la fin de l'import.
                    this.refreshCounters(false);
                    this.checkStalled();
                    return !this.pollingTimedOut;
                }),
                catchError(() => {
                    this.giveUpPolling();
                    return EMPTY;
                })
            )),
            catchError(err => {
                this.onRunLaunchFailed(err, this.translate.instant('etl.preview.search.launchFailedImport'));
                return of(null);
            })
        ).subscribe({
            complete: () => {
                this.configuratorTaskService.getTaskUsingGET(this.datastore, this.task.id).subscribe({
                    error: () => {
                        // L'état final de la tâche est inconnu : mieux vaut le dire que laisser
                        // l'écran sur des chiffres d'avant l'import.
                        this.messageService.add({
                            severity: 'warn',
                            summary: this.translate.instant('etl.preview.search.unknownTaskStatusSummary'),
                            detail: this.translate.instant('etl.preview.search.unknownTaskStatusDetail')
                        });
                    },
                    next: t => {
                        this.setTask(t);
                        // L'import a changé l'état des lignes (IMPORTED exclues de la grille,
                        // valeurs purgées - DMP2-819) : sans ce rafraîchissement explicite, l'écran
                        // continue d'afficher la page chargée avant l'import.
                        this.updateType();
                        this.refreshCounters(true);
                        this.loadFirstPage();
                        // Plus de revalidation enchaînée d'office : elle relançait un traitement
                        // long sans que personne ne l'ait demandé. Le récapitulatif indique quoi
                        // faire des lignes restantes, et l'utilisateur choisit.
                    }
                });
            }
        });
    }

    /**
     * Persiste la correction d'une cellule par un appel ciblé (DMP2-817).
     *
     * L'écran affectait auparavant la valeur en mémoire et comptait sur un « Enregistrer »
     * ultérieur : cliquer « Vérifier » ou « Importer » sans avoir enregistré perdait
     * silencieusement les corrections, ces actions relisant la base côté engine. Avec un appel par
     * cellule, le piège disparaît par construction.
     */
    updateData(row: ScreenRow, indexData: number, $event: any) {
        const previous = row.values[indexData] ? row.values[indexData].destination : null;
        const value = $event === '' ? null : $event;
        if (row.values[indexData]) {
            row.values[indexData].destination = value;
        }
        const patch: { [key: string]: string } = {};
        patch[String(indexData)] = value;
        this.engineEtlService.patchTaskRow(this.datastore, this.task.id, row.line.rowIndex, { values: patch })
            .subscribe({
                next: updated => {
                    row.line = updated;
                    // La correction remet la ligne en attente de chargement : les compteurs et la
                    // ventilation par type d'erreur changent. Espacé, pour ne pas déclencher un
                    // agrégat par cellule quand l'admin en corrige plusieurs d'affilée.
                    this.refreshCounters(false);
                },
                error: err => {
                    // Rétablit l'affichage : la base n'a pas pris la correction.
                    if (row.values[indexData]) {
                        row.values[indexData].destination = previous;
                    }
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('etl.preview.stopTask.error.summary'),
                        detail: err?.error?.detail || this.translate.instant('etl.preview.search.rowCorrectionRejected', { line: row.line.rowIndex })
                    });
                }
            });
    }

    /**
     * Changement du 1er filtre (« Lignes affichées ») : le 2e filtre (« Type d'erreur ») est
     * resynchronisé pour rester cohérent avec le périmètre choisi, plutôt que de garder une
     * sélection héritée d'un autre mode.
     */
    onRowFilterModeChange() {
        this.errorTypeFilter = this.rowFilterMode === 'errorOnly'
            ? EtlTaskPreviewComponent.ERROR_TYPE_ALL
            : null;
        this.loadFirstPage();
    }

    /**
     * Changement du 2e filtre (« Type d'erreur ») : choisir « Toutes les erreurs » ou un type
     * précis ne restitue que des lignes en erreur, donc le 1er filtre doit basculer sur « En
     * erreur uniquement » s'il ne l'est pas déjà.
     */
    onErrorTypeFilterChange() {
        if (this.errorTypeFilter !== null && this.rowFilterMode !== 'errorOnly') {
            this.rowFilterMode = 'errorOnly';
        }
        this.loadFirstPage();
    }

    loadFirstPage() {
        this.cursors = [];
        this.loadPage(null);
    }

    nextPage() {
        if (!this.hasMore || this.pageRows.length === 0) {
            return;
        }
        const after = this.pageRows[this.pageRows.length - 1].line.rowIndex;
        this.cursors.push(after);
        this.loadPage(after);
    }

    prevPage() {
        if (this.cursors.length === 0) {
            return;
        }
        this.cursors.pop();
        const after = this.cursors.length > 0 ? this.cursors[this.cursors.length - 1] : null;
        this.loadPage(after);
    }

    get isFirstPage(): boolean {
        return this.cursors.length === 0;
    }

    /**
     * Va directement à une ligne du fichier source. Remplace le saut à une page arbitraire, que la
     * pagination par keyset ne permet pas — et qui a moins de sens pour un admin qui cherche une
     * ligne précise qu'un numéro de page dans 7 300 pages.
     */
    goToRow() {
        if (this.goToRowIndex === null || this.goToRowIndex === undefined) {
            return;
        }
        const after = Math.max(this.goToRowIndex - 1, -1);
        this.cursors = [after];
        this.loadPage(after);
    }

    private loadPage(after: number | null) {
        this.loadingRows = true;
        const statuses = EtlTaskPreviewComponent.ROW_FILTER_STATUSES[this.rowFilterMode] || [];
        // « Toutes les erreurs » ne restreint pas par clé : seul le statut ERROR (via `statuses`)
        // filtre, comme pour « Aucun filtre ».
        const errorKey = this.errorTypeFilter === EtlTaskPreviewComponent.ERROR_TYPE_ALL ? null : this.errorTypeFilter;
        this.engineEtlService.getTaskRows(
            this.datastore, this.id, statuses.length ? statuses : undefined, errorKey, after, this.pageSize
        ).subscribe({
                next: page => {
                    this.pageDataFile = page.dataFile;
                    this.totalFiltered = page.total || 0;
                    this.hasMore = page.hasMore;
                    const rows = page.dataFile && page.dataFile.dataRows ? page.dataFile.dataRows : [];
                    this.pageRows = (page.lines || []).map((line, i) => ({ line, values: rows[i] || [] }));
                    this.updateTable();
                    this.loadingRows = false;
                },
                error: () => {
                    this.pageRows = [];
                    this.loadingRows = false;
                    // Tant que la tâche est encore IN_PROGRESS, cet appel peut échouer parce que le
                    // fichier est encore en cours d'extraction/chargement : le prochain tick de
                    // polling (cf. getData) le rechargera avec succès. N'alarmer que sur un échec
                    // qui ne s'explique pas par une tâche encore en cours.
                    if (this.task?.status !== 'IN_PROGRESS') {
                        this.messageService.add({
                            severity: 'error',
                            summary: this.translate.instant('etl.preview.stopTask.error.summary'),
                            detail: this.translate.instant('etl.preview.search.unableToLoadRows')
                        });
                    }
                }
            });
    }

    /**
     * Recharge les compteurs, en respectant l'espacement minimal sauf si l'appelant a une raison
     * de les vouloir tout de suite (changement de statut, correction d'une ligne).
     */
    private refreshCounters(force: boolean) {
        const now = Date.now();
        const minInterval = this.processing
            ? EtlTaskPreviewComponent.IMPORT_COUNTERS_REFRESH_MIN_MS
            : EtlTaskPreviewComponent.COUNTERS_REFRESH_MIN_MS;
        if (!force && now - this.lastCountersRefresh < minInterval) {
            return;
        }
        this.lastCountersRefresh = now;
        this.loadCounters();
    }

    private loadCounters() {
        const seq = ++this.countersSeq;
        this.engineEtlService.getTaskRowCounters(this.datastore, this.id).subscribe({
            next: counters => {
                // Une réponse partie avant une plus récente ne doit pas l'écraser.
                if (seq < this.countersAppliedSeq) {
                    return;
                }
                this.countersAppliedSeq = seq;
                this.countersStale = false;
                this.counters = counters;
                this.countersReceivedAt = Date.now();
                this.buildErrorTypeOptions();
                this.recomputeRunView();
                if (this.summaryAfterSeq !== null && seq > this.summaryAfterSeq && !this.processing) {
                    this.runSummary = this.buildRunSummary(counters);
                    this.summaryAfterSeq = null;
                }
            },
            error: () => {
                // Les chiffres déjà affichés ne sont pas effacés : pendant un traitement, les perdre
                // viderait la carte de progression à chaque hoquet. Ils sont simplement signalés
                // comme datés.
                this.countersStale = true;
                this.recomputeRunView();
            }
        });
    }

    /**
     * Vrai tant qu'un traitement tourne sur la tâche : extraction, validation ou import des lignes.
     * L'écran bascule alors en mode traitement — carte de progression et bouton « Arrêter »
     * seulement, sans filtres, tableau ni pagination, dont les lignes changent d'état sous les yeux.
     */
    get processing(): boolean {
        return !this.isExport && this.task?.status === 'IN_PROGRESS' && !this.pollingTimedOut;
    }

    /** Exécution affichée par la carte de progression, recalculée à chaque relevé. */
    runView: RunView | null = null;
    /** Fil des étapes de l'import, repris de la fenêtre de transfert du fichier. */
    runSteps: ImportStep[] = [];
    /** Vrai quand le dernier relevé de compteurs a échoué : les chiffres affichés datent d'avant. */
    countersStale = false;
    /** Récapitulatif de la dernière exécution suivie sur cet écran, jusqu'à ce que l'utilisateur le ferme. */
    runSummary: RunSummary | null = null;

    /**
     * Exécution lancée depuis cet écran. Sert de repli tant que le serveur ne la rapporte pas encore
     * (juste après le clic), ou s'il ne le peut pas (tenant sans les colonnes de suivi d'exécution).
     */
    private localRun: { phase: RunPhase; baseline: ImportLineCountersDto | null; startedAt: number } | null = null;
    private wasProcessing = false;
    private lastRunView: RunView | null = null;
    /** Relevé de départ de l'exécution locale, conservé pour le récapitulatif une fois celle-ci terminée. */
    private endedLocalBaseline: ImportLineCountersDto | null = null;
    private countersSeq = 0;
    private countersAppliedSeq = 0;
    private countersReceivedAt = 0;
    /** Le récapitulatif attend un relevé de compteurs demandé après la fin (numéro de requête strictement supérieur). */
    private summaryAfterSeq: number | null = null;
    private lastActivitySignature = '';
    private lastActivityAt = Date.now();

    /** Tolérance d'horloge entre navigateur et base pour rattacher une exécution serveur au clic local. */
    private static readonly RUN_CLOCK_TOLERANCE_MS = 10000;

    private startLocalRun(phase: RunPhase) {
        this.runSummary = null;
        this.summaryAfterSeq = null;
        this.localRun = { phase, baseline: this.counters ? { ...this.counters, run: undefined } : null, startedAt: Date.now() };
        this.lastActivityAt = Date.now();
        this.task.status = 'IN_PROGRESS';
        this.recomputeRunView();
    }

    /** Le lancement lui-même a échoué : pas de traitement à récapituler. */
    private onRunLaunchFailed(err: any, fallback: string) {
        this.cancelRunTracking();
        this.task.status = 'WAIT';
        this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('etl.preview.search.launchFailedSummary'),
            detail: err?.error?.detail || err?.error?.title || fallback
        });
        this.getData(this.id).catch(() => undefined);
    }

    private cancelRunTracking() {
        this.localRun = null;
        this.endedLocalBaseline = null;
        this.wasProcessing = false;
        this.summaryAfterSeq = null;
        this.runView = null;
    }

    /**
     * Recalcule la carte, et détecte la fin d'un traitement suivi : le récapitulatif est alors
     * construit sur le prochain relevé de compteurs, pris après la fin.
     */
    private recomputeRunView() {
        if (this.processing) {
            this.runView = this.buildRunView();
            this.runSteps = this.buildSteps(this.runView);
            this.lastRunView = this.runView;
            this.wasProcessing = true;
            const signature = `${this.runView.phase}|${this.runView.processed}|${this.counters?.pending}|${this.counters?.errors}`;
            if (signature !== this.lastActivitySignature) {
                this.lastActivitySignature = signature;
                this.lastActivityAt = Date.now();
            }
            return;
        }
        this.runView = null;
        this.runSteps = [];
        if (this.wasProcessing) {
            this.wasProcessing = false;
            this.endedLocalBaseline = this.localRun?.baseline || null;
            this.localRun = null;
            if (!this.pollingTimedOut && this.task) {
                this.summaryAfterSeq = this.countersSeq;
                this.loadCounters();
            }
        }
    }

    /**
     * Situe l'exécution en cours dans le parcours complet. Le transfert est forcément derrière nous :
     * sans fichier reçu, la tâche n'existerait pas.
     *
     * La validation n'est pas une étape du parcours mais une reprise de la précédente : elle apparaît
     * en précision sous « Import des lignes », qu'elle prépare.
     */
    private buildSteps(run: RunView): ImportStep[] {
        const counters = this.counters || {};
        const hasLines = (counters.total || 0) > 0;
        const importDone = hasLines && (counters.pending || 0) === 0 && (counters.imported || 0) > 0;
        return [
            { id: 'UPLOAD', label: this.translate.instant('etl.list.uploadStepTransfer'), state: 'done' },
            {
                id: 'EXTRACTION',
                label: this.translate.instant('etl.list.uploadStepRead'),
                state: run.phase === 'EXTRACTION' ? 'current' : hasLines ? 'done' : 'todo'
            },
            {
                id: 'LOAD',
                label: this.translate.instant('etl.list.uploadStepImport'),
                state: run.phase === 'LOAD' ? 'current' : importDone ? 'done' : 'todo',
                hint: run.phase === 'VALIDATION' ? this.translate.instant('etl.preview.runView.validationHint') : null
            }
        ];
    }

    private buildRunView(): RunView {
        const counters = this.counters || {};
        const server = counters.run && counters.run.running ? counters.run : null;
        const local = this.localRun;
        // Juste après le clic, le serveur peut encore rapporter l'exécution précédente : une
        // exécution serveur ne vaut que si sa phase et son lancement correspondent au clic.
        const serverMatchesLocal = !!server && (!local || (server.phase === local.phase
            && (server.elapsedSeconds || 0) * 1000 <= Date.now() - local.startedAt + EtlTaskPreviewComponent.RUN_CLOCK_TOLERANCE_MS));
        if (serverMatchesLocal) {
            const sinceReceipt = Math.max(0, Math.round((Date.now() - this.countersReceivedAt) / 1000));
            return this.finishRunView({
                phase: server.phase,
                fromServer: true,
                toProcess: server.toProcess ?? null,
                processed: server.processed || 0,
                imported: Math.max(0, (counters.imported || 0) - (server.importedAtStart || 0)),
                errors: Math.max(0, (counters.errors || 0) - (server.errorsAtStart || 0)),
                locked: server.locked || 0,
                elapsedSeconds: (server.elapsedSeconds || 0) + sinceReceipt
            }, server.elapsedSeconds || 0);
        }
        if (local) {
            const baseline = local.baseline;
            const elapsed = Math.round((Date.now() - local.startedAt) / 1000);
            // Seul l'import se mesure depuis le navigateur : chaque ligne y quitte PENDING. Une
            // validation réécrit des lignes sans changer forcément leur état.
            const toProcess = local.phase === 'LOAD' && baseline ? (baseline.pending || 0) : null;
            return this.finishRunView({
                phase: local.phase,
                fromServer: false,
                toProcess,
                processed: toProcess === null ? 0 : Math.max(0, toProcess - (counters.pending || 0)),
                imported: baseline ? Math.max(0, (counters.imported || 0) - (baseline.imported || 0)) : 0,
                errors: baseline ? Math.max(0, (counters.errors || 0) - (baseline.errors || 0)) : 0,
                locked: 0,
                elapsedSeconds: elapsed
            }, elapsed);
        }
        return this.finishRunView({
            phase: null, fromServer: false, toProcess: null, processed: 0, imported: 0, errors: 0, locked: 0,
            elapsedSeconds: null
        }, 0);
    }

    private finishRunView(base: Omit<RunView, 'progress' | 'speedLpm' | 'etaSeconds' | 'title' | 'subtitle' | 'caption'
                              | 'stats' | 'errorRate' | 'errorCaption'>,
                          speedElapsedSeconds: number): RunView {
        const counters = this.counters || {};
        const speedLpm = base.processed > 0 && speedElapsedSeconds >= 5
            ? Math.round((base.processed / speedElapsedSeconds) * 60) : null;
        const etaSeconds = speedLpm && base.toProcess !== null
            ? Math.max(0, Math.round(((base.toProcess - base.processed) / speedLpm) * 60)) : null;
        const progress = base.toProcess === null ? null
            : base.toProcess <= 0 ? 100 : Math.min(100, Math.round((base.processed / base.toProcess) * 100));
        const fmt = (n: number) => (n || 0).toLocaleString('fr-FR');
        const speed = { label: this.translate.instant('etl.preview.runView.speedLabel'),
            value: speedLpm ? this.translate.instant('etl.preview.runView.speedValue', { speed: fmt(speedLpm) }) : '—' };
        const eta = { label: this.translate.instant('etl.preview.runView.etaLabel'),
            value: etaSeconds !== null ? this.translate.instant('etl.preview.runView.etaValue', { eta: this.formatEta(etaSeconds) }) : '—' };
        const elapsed = { label: this.translate.instant('etl.preview.runView.elapsedLabel'),
            value: base.elapsedSeconds !== null ? this.formatEta(base.elapsedSeconds) : '—' };

        let title: string;
        let subtitle: string;
        let stats: RunStat[];
        switch (base.phase) {
            case 'LOAD':
                title = this.translate.instant('etl.preview.runView.loadTitle');
                subtitle = this.translate.instant('etl.preview.runView.loadSubtitle');
                stats = [
                    { label: this.translate.instant('etl.preview.runView.statImported'), value: fmt(base.imported), tone: 'success' },
                    { label: this.translate.instant('etl.preview.runView.statInError'), value: fmt(base.errors), tone: base.errors > 0 ? 'error' : null },
                    { label: this.translate.instant('etl.preview.runView.statRemaining'), value: fmt(counters.pending) },
                    speed, eta, elapsed
                ];
                break;
            case 'VALIDATION':
                title = this.translate.instant('etl.preview.runView.validationTitle');
                subtitle = base.fromServer ? this.translate.instant('etl.preview.runView.validationSubtitleFromServer')
                    : this.translate.instant('etl.preview.runView.validationSubtitleLocal');
                stats = [
                    { label: this.translate.instant('etl.preview.runView.statReadyToImport'), value: fmt(counters.pending) },
                    { label: this.translate.instant('etl.preview.runView.statInError'), value: fmt(counters.errors), tone: (counters.errors || 0) > 0 ? 'error' : null },
                    speed, eta, elapsed
                ];
                break;
            case 'EXTRACTION':
                title = this.translate.instant('etl.preview.runView.extractionTitle');
                subtitle = this.translate.instant('etl.preview.runView.extractionSubtitle');
                stats = [
                    { label: this.translate.instant('etl.preview.runView.statLinesRead'), value: fmt(base.processed) },
                    { label: this.translate.instant('etl.preview.runView.statLinesInError'), value: fmt(base.errors), tone: base.errors > 0 ? 'error' : null },
                    speed, elapsed
                ];
                break;
            default:
                title = this.translate.instant('etl.preview.runView.defaultTitle');
                subtitle = this.translate.instant('etl.preview.runView.defaultSubtitle');
                stats = [];
        }
        const caption = base.toProcess !== null
            ? this.translate.instant('etl.preview.runView.captionWithTotal', { processed: fmt(base.processed), total: fmt(base.toProcess) })
            : base.phase === 'EXTRACTION' ? this.translate.instant('etl.preview.runView.captionRead', { processed: fmt(base.processed) }) : null;
        const quality = this.errorShare(base.phase, base.processed, base.errors, counters);
        return { ...base, progress, speedLpm, etaSeconds, title, subtitle, caption, stats,
            errorRate: quality.rate, errorCaption: quality.caption };
    }

    /**
     * Part des lignes en erreur parmi celles traitées par l'exécution : deuxième barre de la carte.
     *
     * Le rapport n'a pas le même sens selon la phase. À la lecture du fichier et au chargement, une
     * ligne traitée est soit acceptée soit rejetée, et le compte d'erreurs de l'exécution est le bon
     * numérateur. À la validation, une ligne en erreur peut redevenir valide : ce qui compte est
     * l'état courant des lignes non importées, pas l'écart depuis le lancement.
     */
    private errorShare(phase: RunPhase | null, processed: number, errors: number,
                       counters: ImportLineCountersDto): { rate: number | null; caption: string | null } {
        const fmt = (n: number) => (n || 0).toLocaleString('fr-FR');
        let numerator: number;
        let denominator: number;
        let subject: string;
        if (phase === 'VALIDATION') {
            numerator = counters.errors || 0;
            denominator = (counters.pending || 0) + (counters.errors || 0);
            subject = this.translate.instant('etl.preview.runView.errorShareValidationSubject');
        } else if (phase === 'EXTRACTION' || phase === 'LOAD') {
            numerator = errors;
            denominator = processed;
            subject = phase === 'EXTRACTION' ? this.translate.instant('etl.preview.runView.errorShareReadSubject')
                : this.translate.instant('etl.preview.runView.errorShareProcessedSubject');
        } else {
            return { rate: null, caption: null };
        }
        if (denominator <= 0) {
            return { rate: null, caption: null };
        }
        const rate = Math.min(100, Math.round((numerator / denominator) * 100));
        return { rate, caption: this.translate.instant('etl.preview.runView.errorShareCaption',
            { rate, subject, numerator: fmt(numerator), denominator: fmt(denominator) }) };
    }

    /**
     * Récapitulatif d'une exécution terminée, sur des compteurs relevés après sa fin. Indique aussi
     * quoi faire des lignes restantes, puisque plus rien n'est enchaîné d'office.
     */
    private buildRunSummary(counters: ImportLineCountersDto): RunSummary {
        const phase = this.lastRunView?.phase ?? null;
        const run = counters.run && !counters.run.running && counters.run.phase === phase ? counters.run : null;
        const start = run
            ? { imported: run.importedAtStart || 0, errors: run.errorsAtStart || 0 }
            : this.endedLocalBaseline;
        const imported = start ? Math.max(0, (counters.imported || 0) - (start.imported || 0)) : null;
        const errors = start ? Math.max(0, (counters.errors || 0) - (start.errors || 0)) : null;
        const locked = run?.locked || 0;
        const elapsedSeconds = run ? run.elapsedSeconds : this.lastRunView?.elapsedSeconds;
        const processed = run ? run.processed : this.lastRunView?.processed;
        const pending = counters.pending || 0;
        const errorsTotal = counters.errors || 0;
        const status = this.task?.status;
        const fmt = (n: number) => (n || 0).toLocaleString('fr-FR');
        const duration = elapsedSeconds !== null && elapsedSeconds !== undefined
            ? this.translate.instant('etl.preview.runSummary.durationSuffix', { duration: this.formatEta(elapsedSeconds) }) : '';

        let severity: RunSummary['severity'];
        let title: string;
        const lines: string[] = [];
        if (status === 'ERROR') {
            severity = 'error';
            title = this.translate.instant(phase === 'LOAD' ? 'etl.preview.runSummary.loadFailedTitle'
                : phase === 'VALIDATION' ? 'etl.preview.runSummary.validationFailedTitle' : 'etl.preview.runSummary.genericFailedTitle');
            lines.push(this.translate.instant('etl.preview.runSummary.failedDetail'));
        } else if (status === 'INTERRUPTED') {
            severity = 'warn';
            title = this.translate.instant(phase === 'LOAD' ? 'etl.preview.runSummary.loadInterruptedTitle'
                : phase === 'VALIDATION' ? 'etl.preview.runSummary.validationInterruptedTitle' : 'etl.preview.runSummary.genericInterruptedTitle');
        } else {
            title = this.translate.instant(phase === 'LOAD' ? 'etl.preview.runSummary.loadDoneTitle'
                : phase === 'VALIDATION' ? 'etl.preview.runSummary.validationDoneTitle'
                : phase === 'EXTRACTION' ? 'etl.preview.runSummary.extractionDoneTitle' : 'etl.preview.runSummary.genericDoneTitle');
            severity = locked > 0 ? 'warn' : pending === 0 && errorsTotal === 0 ? 'success' : 'info';
        }

        if (phase === 'LOAD' && imported !== null) {
            lines.unshift(this.translate.instant('etl.preview.runSummary.loadResultLine',
                { imported: fmt(imported), errors: fmt(errors), duration }));
        } else if (phase === 'EXTRACTION' && processed !== null && processed !== undefined) {
            lines.unshift(this.translate.instant('etl.preview.runSummary.extractionResultLine',
                { processed: fmt(processed), errors: fmt(errors), duration }));
        } else if (duration) {
            lines.unshift(this.translate.instant(
                phase === 'VALIDATION' ? 'etl.preview.runSummary.doneValidationLine' : 'etl.preview.runSummary.doneGenericLine',
                { duration }));
        }
        if (locked > 0) {
            lines.push(this.translate.instant('etl.preview.runSummary.lockedRowsLine', { count: fmt(locked) }));
        }

        const actions: RunSummaryAction[] = [];
        if (errorsTotal > 0) {
            lines.push(this.translate.instant('etl.preview.runSummary.errorsRemainingLine',
                { count: fmt(errorsTotal), validationLabel: this.translate.instant('etl.preview.footer.validation') }));
            actions.push({ id: 'showErrors', label: this.translate.instant('etl.preview.runSummary.showErrorsAction'),
                icon: 'pi pi-filter', requiresRun: false });
        }
        if (pending > 0) {
            lines.push(this.translate.instant('etl.preview.runSummary.pendingLine', { count: fmt(pending) }));
            actions.push({
                id: 'import',
                label: phase === 'LOAD' ? this.translate.instant('etl.preview.runSummary.importRemainingAction')
                    : this.translate.instant('etl.export.search.import'),
                icon: 'pi pi-upload', requiresRun: true
            });
        }
        const icon = severity === 'success' ? 'pi-check-circle' : severity === 'error' ? 'pi-times-circle'
            : severity === 'warn' ? 'pi-exclamation-triangle' : 'pi-info-circle';
        return { severity, icon, title, lines, actions };
    }

    onSummaryAction(action: RunSummaryAction) {
        if (action.id === 'showErrors') {
            this.rowFilterMode = 'errorOnly';
            this.onRowFilterModeChange();
        } else if (action.id === 'import' && this.canImport) {
            this.doImport();
        }
    }

    formatEta(totalSeconds: number): string {
        if (totalSeconds === null || totalSeconds === undefined) return '';
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        if (hours > 0) return `${hours} h ${minutes} min`;
        if (minutes > 0) return `${minutes} min ${seconds} s`;
        return `${seconds} s`;
    }

    /**
     * Options du filtre « Type d'erreur », bâties sur la ventilation renvoyée par le serveur.
     *
     * Les clés viennent du même code que celles portées par chaque ligne
     * (DataFileTransformService#buildErrorFilterKey) : c'est ce qui garantit que filtrer sur une
     * option retrouve bien les lignes correspondantes. L'écran dérivait ces options du détail ligne
     * à ligne pour éviter un décalage avec des résumés calculés ailleurs ; une fois paginé il ne
     * peut plus tenir ce détail, la garantie est donc portée côté serveur.
     */
    private buildErrorTypeOptions() {
        const options: SelectItem[] = [
            { label: this.translate.instant('etl.preview.errorsByType.noFilter'), value: null },
            { label: this.translate.instant('etl.preview.errorsByType.allErrors'), value: EtlTaskPreviewComponent.ERROR_TYPE_ALL }
        ];
        const byKey = this.counters && this.counters.countByErrorKey ? this.counters.countByErrorKey : {};
        Object.keys(byKey)
            .map(key => ({ key, label: this.errorTypeLabel(key), count: byKey[key] }))
            .sort((a, b) => a.label.localeCompare(b.label))
            .forEach(entry => options.push({
                label: `${entry.label} (${entry.count})`,
                value: entry.key
            }));
        this.errorTypeOptions = options;
        if (!options.some(o => o.value === this.errorTypeFilter)) {
            this.errorTypeFilter = null;
        }
    }

    /**
     * Durée d'une exécution de l'historique, pour la colonne « Durée ». Vide si le début n'est pas
     * connu (entrée antérieure à l'introduction de {@code TaskCompteur.dateExec}).
     */
    durationBetween(start: Date | string, end: Date | string): string {
        if (!start || !end) {
            return '';
        }
        const ms = moment(end).diff(moment(start));
        if (ms < 0) {
            return '';
        }
        // Sous la seconde, arrondir à la seconde était trompeur : deux horodatages affichés
        // identiques (précision seconde) pouvaient donner « 1s » ou « 0s » selon le hasard des
        // millisecondes. En dessous d'1s, autant l'afficher telle quelle.
        if (ms < 1000) {
            return `${ms}ms`;
        }
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        if (hours > 0) {
            return `${hours}h ${minutes}min ${seconds}s`;
        }
        if (minutes > 0) {
            return `${minutes}min ${seconds}s`;
        }
        return `${seconds}s`;
    }

    /** Vrai s'il y a au moins un type d'erreur à afficher dans le panneau de synthèse. */
    hasErrorTypes(): boolean {
        return !!this.counters && !!this.counters.countByErrorKey
            && Object.keys(this.counters.countByErrorKey).length > 0;
    }

    /** Libellé d'un type d'erreur, pour le panneau de synthèse (le filtre y ajoute le compte). */
    errorTypeLabelFor(key: string): string {
        return this.errorTypeLabel(key);
    }

    private errorTypeLabel(key: string): string {
        if (!key) {
            // errorDetails[].key est optionnel côté DTO (cas théorique, cf. buildErrorFilterKey côté
            // serveur) ; countByErrorKey n'appelle jamais ici avec une clé vide.
            return key;
        }
        if (key.startsWith('CAT:')) {
            const catKey = key.substring('CAT:'.length);
            return this.translate.instant('etl.common.' + catKey) || catKey;
        }
        if (key.startsWith('FIELD:')) {
            return this.translate.instant('etl.preview.errorsByType.fieldLabel', { field: key.substring('FIELD:'.length) });
        }
        return key;
    }

    private buildTootltip(table: TableLiteDto, field?: FieldTable) {
        const name = this.translate.instant('common.Name');
        const code = this.translate.instant('common.Code');
        const description = this.translate.instant('common.Description');
        let txt = `# ${this.translate.instant('etl.preview.table.tooltip.tableSection')}\n`;
        if (table.label) { txt += `- ${name} : ${table.label}\n`; }
        if (table.code) { txt += `- ${code} : ${table.code}\n`; }
        if (table.description) { txt += `- ${description} : ${table.description}\n`; }
        if (field) {
            txt += `# ${this.translate.instant('etl.preview.table.tooltip.fieldSection')}\n`;
            if (field.label) { txt += `- ${name} : ${field.label}\n`; }
            if (field.code) { txt += `- ${code} : ${field.code}\n`; }
            txt += `- ${this.translate.instant('etl.preview.table.tooltip.required')}: `
                + `${field.notNull ? this.translate.instant('common.True') : this.translate.instant('common.False')}\n`;
            if (field.description) { txt += `- ${description} : ${field.description}\n`; }
            if (field.categoryField) {
                txt += `- ${this.translate.instant('etl.preview.table.tooltip.category')} : ${this.etlUpdateDesignService.getCategory(field)}\n`;
            }
            if (field.type) {
                txt += `- ${this.translate.instant('etl.preview.table.tooltip.type')} : ${this.etlUpdateDesignService.getType(field)}`;
            }
        }
        return txt;
    }

    protected readonly Object = Object;

    stopTask() {
        if(!this.isExport && this.task.status !== 'IN_PROGRESS'){
            return;
        }
        this.confirmationService.confirm({
            header: this.translate.instant('etl.preview.stopTask.header'),
            acceptLabel: this.translate.instant('common.True'),
            rejectLabel: this.translate.instant('common.False'),
            message: this.translate.instant('etl.preview.stopTask.confirm'),
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.configuratorTaskService.stopTask(this.datastore, this.task.id).subscribe({
                    next: () => {
                        this.messageService.add({
                            severity: 'info',
                            summary: this.translate.instant('etl.preview.stopTask.success.summary'),
                            detail: this.translate.instant('etl.preview.stopTask.success.detail')
                        });
                    },
                    error: (err) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: this.translate.instant('etl.preview.stopTask.error.summary'),
                            detail: this.translate.instant('etl.preview.stopTask.error.detail')
                        });
                    }
                });
            }
        });
    }

    // ============================================================
    // Recherche / remplacement de masse (DMP2-818)
    //
    // Le moteur vit côté serveur : l'écran ne voit qu'une page de lignes, une recherche locale ne
    // trouverait que ce qui y est affiché et le « tout remplacer » ne toucherait que cette page.
    // L'écran compose donc les critères, affiche les compteurs renvoyés — exacts sur TOUTE la tâche —
    // et une page d'occurrences à relire avant application.
    // ============================================================

    /** Occurrences demandées par page. Le serveur en renvoie 200 au maximum. */
    private static readonly MATCH_PAGE_SIZE = 50;

    searchDialogVisible = false;
    /** Choix explicite plutôt que déduit d'autres champs : ce qui compte reste visible à l'écran. */
    searchType: 'text' | 'empty' = 'text';
    /** D'où vient la nouvelle valeur : un texte saisi, ou la valeur d'une autre colonne, ligne par ligne. */
    replaceValueSource: 'manual' | 'column' = 'manual';
    searchCriteria: ImportLineSearchCriteriaDto = EtlTaskPreviewComponent.emptyCriteria();
    searchTypeOptions: SelectItem[] = [
        { label: this.translate.instant('etl.preview.search.textOption'), value: 'text' },
        { label: this.translate.instant('etl.preview.search.emptyOption'), value: 'empty' }
    ];
    replaceTypeOptions: SelectItem[] = [
        { label: this.translate.instant('etl.preview.search.firstOccurrenceOption'), value: 'first' },
        { label: this.translate.instant('etl.preview.search.allOccurrencesOption'), value: 'all' },
        { label: this.translate.instant('etl.preview.search.overwriteOption'), value: 'overwrite' }
    ];
    replaceValueSourceOptions: SelectItem[] = [
        { label: this.translate.instant('etl.preview.search.manualTextOption'), value: 'manual' },
        { label: this.translate.instant('etl.preview.search.columnValueOption'), value: 'column' }
    ];
    /** Périmètre : les lignes déjà importées sont exclues côté serveur dans tous les cas. */
    searchScopeOptions: SelectItem[] = [
        { label: this.translate.instant('etl.preview.toolbar.allRows'), value: null },
        { label: this.translate.instant('etl.preview.toolbar.pendingOnly'), value: 'PENDING' },
        { label: this.translate.instant('etl.preview.toolbar.errorOnly'), value: 'ERROR' }
    ];
    searchableColumns: SelectItem[] = [];
    searchResults: ImportLineSearchResultsDto = null;
    searchMatches: SearchMatchRow[] = [];
    searching = false;
    replacing = false;
    selectAllMatches = false;

    private static emptyCriteria(): ImportLineSearchCriteriaDto {
        return {
            searchTerm: '',
            selectedColumns: [],
            caseSensitive: false,
            replaceWith: '',
            searchEmpty: false,
            filterColumnIndex: null,
            filterValue: '',
            targetColumnIndex: null,
            valueSourceColumnIndex: null,
            replaceType: 'all',
            status: null
        };
    }

    /** Colonnes proposées à la recherche : les mêmes que celles du tableau, aux mêmes positions. */
    private prepareSearchableColumns() {
        this.searchableColumns = (this.cols || []).map(col => ({
            label: `${col.field.label || col.source} (${col.source})`,
            value: col.indexData,
            title: col.tooltip
        }));
    }

    /** Rechercher n'a de sens ni pendant un traitement, ni sur une tâche sans ligne à traiter. */
    get canSearchReplace(): boolean {
        return !this.processing && !!this.counters && (this.counters.total || 0) > 0
            && this.searchableColumns.length > 0;
    }

    openSearchDialog() {
        this.resetSearch();
        // Toutes les colonnes par défaut : le cas courant est de chercher une valeur sans savoir
        // dans quelle colonne elle se trouve.
        this.searchCriteria.selectedColumns = this.searchableColumns.map(c => c.value);
        // Le périmètre suit le filtre déjà posé sur le tableau, qui dit ce que l'utilisateur regarde.
        this.searchCriteria.status = this.rowFilterMode === 'pendingOnly' ? 'PENDING'
            : this.rowFilterMode === 'errorOnly' ? 'ERROR' : null;
        this.searchDialogVisible = true;
    }

    closeSearchDialog() {
        this.searchDialogVisible = false;
        this.resetSearch();
    }

    private resetSearch() {
        this.searchCriteria = EtlTaskPreviewComponent.emptyCriteria();
        this.searchType = 'text';
        this.replaceValueSource = 'manual';
        this.searchResults = null;
        this.searchMatches = [];
        this.selectAllMatches = false;
    }

    /** Chercher du vide n'a rien à substituer dans la cellule : le remplacement l'écrase forcément. */
    onSearchTypeChange() {
        this.searchCriteria.searchEmpty = this.searchType === 'empty';
        if (this.searchType === 'empty') {
            this.searchCriteria.replaceType = 'overwrite';
        }
        this.invalidateResults();
    }

    /** La valeur vient d'un texte OU d'une colonne, jamais des deux. */
    onValueSourceChange() {
        if (this.replaceValueSource === 'manual') {
            this.searchCriteria.valueSourceColumnIndex = null;
        } else {
            this.searchCriteria.replaceWith = '';
        }
        this.invalidateResults();
    }

    /** Un critère modifié rend les occurrences affichées caduques : elles ne décrivent plus la demande. */
    invalidateResults() {
        this.searchResults = null;
        this.searchMatches = [];
        this.selectAllMatches = false;
    }

    runSearch(append = false) {
        const problem = this.criteriaProblem();
        if (problem) {
            this.messageService.add({ severity: 'warn', summary: this.translate.instant('etl.preview.search.incompleteSearchSummary'), detail: problem });
            return;
        }
        const after = append ? this.searchResults?.nextAfter : null;
        this.searching = true;
        this.engineEtlService.searchTaskRows(this.datastore, this.id, this.searchCriteria, after,
                EtlTaskPreviewComponent.MATCH_PAGE_SIZE)
            .subscribe({
                next: results => {
                    this.searching = false;
                    this.searchResults = results;
                    const page = (results.matches || []).map(match => this.toMatchRow(match));
                    this.searchMatches = append ? [...this.searchMatches, ...page] : page;
                    this.selectAllMatches = false;
                },
                error: err => {
                    this.searching = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('etl.preview.search.searchFailedSummary'),
                        detail: err?.error?.detail || err?.error?.title || this.translate.instant('etl.preview.search.searchFailedDetail')
                    });
                }
            });
    }

    /** Vérification locale du minimum exigé par le serveur, pour éviter un aller-retour inutile. */
    private criteriaProblem(): string | null {
        if (!this.searchCriteria.searchEmpty && !this.searchCriteria.searchTerm?.trim()) {
            return this.translate.instant('etl.preview.search.selectSearchTermOrEmpty');
        }
        if (!this.searchCriteria.selectedColumns?.length) {
            return this.translate.instant('etl.preview.search.selectAtLeastOneColumn');
        }
        if (this.replaceValueSource === 'column'
                && (this.searchCriteria.valueSourceColumnIndex === null
                    || this.searchCriteria.valueSourceColumnIndex === undefined)) {
            return this.translate.instant('etl.preview.search.selectSourceColumn');
        }
        return null;
    }

    private toMatchRow(match: ImportLineSearchMatchDto): SearchMatchRow {
        return {
            ...match,
            selected: false,
            columnName: this.columnLabel(match.columnIndex),
            targetColumnName: this.columnLabel(match.targetColumnIndex)
        };
    }

    columnLabel(index: number): string {
        const col = (this.cols || []).find(c => c.indexData === index);
        return col ? (col.field.label || col.source) : this.translate.instant('etl.preview.search.unknownColumn', { index });
    }

    toggleSelectAllMatches() {
        this.searchMatches.forEach(match => match.selected = this.selectAllMatches);
    }

    get selectedMatchCount(): number {
        return this.searchMatches.filter(match => match.selected).length;
    }

    /** Remplace les occurrences cochées, avec la valeur éventuellement retouchée. */
    replaceSelection() {
        const matches: ImportLineSearchMatchDto[] = this.searchMatches
            .filter(match => match.selected)
            .map(({ selected, columnName, targetColumnName, ...match }) => match);
        if (!matches.length) {
            return;
        }
        this.applyReplace({ criteria: this.searchCriteria, matches });
    }

    /**
     * Remplace partout : le serveur réapplique les critères à toutes les lignes correspondantes.
     * Confirmation obligatoire — l'action porte sur des lignes que l'écran n'a pas montrées.
     */
    replaceAll() {
        const rows = this.searchResults?.affectedRows || 0;
        const occurrences = this.searchResults?.totalOccurrences || 0;
        this.confirmationService.confirm({
            header: this.translate.instant('etl.preview.search.replaceAllHeader'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: this.translate.instant('common.True'),
            rejectLabel: this.translate.instant('common.False'),
            message: this.translate.instant('etl.preview.search.replaceAllConfirm',
                { occurrences: occurrences.toLocaleString('fr-FR'), rows: rows.toLocaleString('fr-FR') }),
            accept: () => this.applyReplace({ criteria: this.searchCriteria })
        });
    }

    private applyReplace(request: { criteria: ImportLineSearchCriteriaDto; matches?: ImportLineSearchMatchDto[] }) {
        this.replacing = true;
        this.engineEtlService.replaceTaskRows(this.datastore, this.id, request).subscribe({
            next: result => {
                this.replacing = false;
                this.searchDialogVisible = false;
                this.resetSearch();
                this.onReplaced(result);
            },
            error: err => {
                this.replacing = false;
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('etl.preview.search.replaceFailedSummary'),
                    detail: err?.error?.detail || err?.error?.title || this.translate.instant('etl.preview.search.replaceFailedDetail')
                });
            }
        });
    }

    /**
     * Les lignes modifiées sont repassées « à importer » avec leurs erreurs vidées : l'écran se
     * resynchronise, et dit que ces lignes n'ont pas encore été revalidées.
     */
    private onReplaced(result: ImportLineReplaceResultDto) {
        this.refreshCounters(true);
        this.loadFirstPage();
        const fmt = (n: number) => (n || 0).toLocaleString('fr-FR');
        const details = [this.translate.instant('etl.preview.search.replacedCellsLine',
            { cells: fmt(result.replacedCells), rows: fmt(result.affectedRows) })];
        if (result.skippedImportedRows) {
            details.push(this.translate.instant('etl.preview.search.skippedImportedLine', { count: fmt(result.skippedImportedRows) }));
        }
        if (result.affectedRows) {
            details.push(this.translate.instant('etl.preview.search.pendingRevalidateLine',
                { validationLabel: this.translate.instant('etl.preview.footer.validation') }));
        }
        this.messageService.add({
            severity: result.affectedRows ? 'success' : 'info',
            summary: this.translate.instant('etl.preview.search.replaceDoneSummary'),
            detail: details.join(' '),
            life: 12000
        });
    }

    // ============================================================
}


export type RunPhase = ImportRunDto.PhaseEnum;

export interface RunStat {
    label: string;
    value: string;
    tone?: 'success' | 'error' | null;
}

/** Exécution affichée par la carte de progression. */
export interface RunView {
    /** `null` : traitement en cours dont la nature n'est pas connue (tenant sans suivi d'exécution). */
    phase: RunPhase | null;
    /** Vrai si les chiffres viennent du serveur : valables pour tous, et après un rechargement. */
    fromServer: boolean;
    /** `null` : total inconnu, barre indéterminée. */
    toProcess: number | null;
    processed: number;
    imported: number;
    errors: number;
    locked: number;
    elapsedSeconds: number | null;
    progress: number | null;
    speedLpm: number | null;
    etaSeconds: number | null;
    title: string;
    subtitle: string;
    caption: string | null;
    /** Part des lignes en erreur, en %, parmi celles traitées ; `null` si rien n'a encore été traité. */
    errorRate: number | null;
    errorCaption: string | null;
    stats: RunStat[];
}

export interface RunSummaryAction {
    id: 'showErrors' | 'import';
    label: string;
    icon: string;
    /** Action qui lance un traitement : réservée aux utilisateurs autorisés à exécuter les imports. */
    requiresRun: boolean;
}

export interface RunSummary {
    severity: 'success' | 'info' | 'warn' | 'error';
    icon: string;
    title: string;
    lines: string[];
    actions: RunSummaryAction[];
}

/** Une occurrence affichée dans le dialogue de recherche : le contrat serveur, plus ce que l'écran y ajoute. */
export interface SearchMatchRow extends ImportLineSearchMatchDto {
    selected: boolean;
    columnName: string;
    targetColumnName: string;
}

/** Une ligne de la page affichée : son état côté serveur et ses valeurs. */
export interface ScreenRow {
    line: ImportLineDto;
    values: Array<any>;
}

export interface ItemCol {
    source: string;
    field: FieldTable;
    indexData: number;
    tooltip?: string;
}
